import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Card, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';
import { posts } from '../../data/posts';

const categories = ['All', ...new Set(posts.map(p => p.cat))];

const BlogCard = ({ p }) => {
    const card = (
        <Card className="blog-card h-100">
            <div className="blog-card-accent" style={{ background: p.c }} />
            <Card.Body className="p-4 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={p.icon} style={{ color: p.c, fontSize: '0.8rem' }} />
                        <span className="blog-badge" style={{ background: p.c + '12', color: p.c }}>{p.cat}</span>
                    </div>
                    <span style={{ fontSize: '0.7rem', color: 'var(--gray)' }}>{p.rt}</span>
                </div>
                <h3 className="blog-title">{p.t}</h3>
                <p className="blog-excerpt">{p.ex}</p>
                <div className="d-flex justify-content-between align-items-center mt-auto pt-3"
                     style={{ borderTop: '1px solid rgba(0,0,0,.06)' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>{p.date}</span>
                    <span className="blog-read-link" style={{ color: p.c }}>
                        Read <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '0.6rem', marginLeft: 3 }} />
                    </span>
                </div>
            </Card.Body>
        </Card>
    );

    if (p.slug) {
        return (
            <Link to={`/blog/${p.slug}`} style={{ textDecoration: 'none' }}>
                {card}
            </Link>
        );
    }
    return card;
};

const BlogPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCat, setSelectedCat] = useState('All');

    const filtered = useMemo(() => {
        return posts.filter(p => {
            const matchesSearch = searchTerm === '' ||
                p.t.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.ex.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.cat.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCat = selectedCat === 'All' || p.cat === selectedCat;
            return matchesSearch && matchesCat;
        });
    }, [searchTerm, selectedCat]);

    const hasFilters = searchTerm !== '' || selectedCat !== 'All';

    return (
        <>
            <SEO
                title="Blog | Nebulyx AI"
                description="Insights on ConsistentAI and governed agents."
                canonicalUrl="https://nebulyx.ai/blog"
            />
            <div className="home-page">
                {/* Sticky filter bar - matches use cases */}
                <div className="sticky-filter-wrapper">
                    <Container>
                        <div className="sticky-filter-bar">
                            <Row className="g-2 align-items-center">
                                <Col lg={6} md={6}>
                                    <InputGroup>
                                        <InputGroup.Text className="filter-icon">
                                            <FontAwesomeIcon icon={faSearch} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search articles..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="filter-input"
                                        />
                                    </InputGroup>
                                </Col>
                                <Col lg={3} md={4}>
                                    <Form.Select
                                        value={selectedCat}
                                        onChange={(e) => setSelectedCat(e.target.value)}
                                        className="filter-input"
                                        style={{ borderRadius: '8px', border: '1px solid #e9ecef' }}
                                    >
                                        {categories.map(c => (
                                            <option key={c} value={c}>
                                                {c === 'All' ? 'All Topics' : c}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col lg={2} md={2} className="d-flex align-items-center gap-2">
                                    {hasFilters && (
                                        <button
                                            className="btn btn-link clear-btn"
                                            onClick={() => { setSearchTerm(''); setSelectedCat('All'); }}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                    <span className="results-badge d-none d-lg-inline-block">
                                        {filtered.length}
                                    </span>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </div>

                <section className="what-is-section" style={{ paddingTop: '2rem' }}>
                    <Container>
                        <Row className="g-4">
                            {filtered.map(p => (
                                <Col md={6} lg={4} key={p.id}>
                                    <BlogCard p={p} />
                                </Col>
                            ))}
                            {filtered.length === 0 && (
                                <Col className="text-center py-4">
                                    <p style={{ color: 'var(--gray)' }}>No articles match your search.</p>
                                </Col>
                            )}
                        </Row>
                        {filtered.length > 0 && hasFilters && (
                            <p className="text-center text-muted mt-3" style={{ fontSize: '0.82rem' }}>
                                Showing {filtered.length} article{filtered.length !== 1 ? 's' : ''}
                                {selectedCat !== 'All' && ` in ${selectedCat}`}
                            </p>
                        )}
                    </Container>
                </section>

                <section className="final-cta-section">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg={8} className="text-center">
                                <h2>Want to See the Platform?</h2>
                                <p className="cta-description">
                                    Book a discovery call - we'll walk you through a real use case.
                                </p>
                                <a
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="btn btn-lg cta-button-hero"
                                    style={{ color: 'var(--primary)' }}
                                >
                                    Book Discovery Call
                                </a>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </div>
        </>
    );
};

export default BlogPage;